import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Bell as BellIcon } from "lucide-react";
import { get } from "~/lib/api";

/**
 * The trailing header controls, shared by the consumer and business chrome
 * (R22 T13/T14).
 */

/**
 * The unread count, on every signed-in screen.
 *
 * The landing page promises "you get a notification in your inbox", and before
 * the revamp nothing in the chrome linked there at all. This is what keeps that
 * promise. It is a link with a count rather than a panel: the panel is the inbox
 * screen's job, and duplicating it in the header would be two renderings of the
 * same list to keep in step.
 *
 * **The count is read without marking anything read.** `/inbox` marks only when
 * asked to (`?markRead=1`), which is exactly why that is a parameter: a header
 * that marked on every poll would zero its own badge and the reader would never
 * learn which items were new.
 *
 * **A failed read shows no badge and never an error.** An unread count is not
 * worth interrupting anybody for, and a header that breaks over one is a far
 * worse outcome than a badge that is briefly absent. That was true of the
 * server-rendered header too, for the same reason.
 */
export function NotificationBell() {
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void get<{ unreadCount: number }>("/inbox").then((result) => {
      if (!cancelled && result.ok) setUnread(result.value.unreadCount);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Link
      to="/inbox"
      aria-label={unread > 0 ? `Inbox, ${unread} unread` : "Inbox"}
      title="Inbox"
      className="relative inline-flex size-9 items-center justify-center rounded-full text-muted transition hover:bg-accent/10 hover:text-accent"
    >
      <BellIcon className="size-5" aria-hidden strokeWidth={1.8} />
      {unread > 0 && (
        <span className="absolute -end-0.5 -top-0.5 inline-flex min-w-4.5 items-center justify-center rounded-md bg-accent px-1 py-0.5 font-mono text-[10px] font-bold text-on-accent">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </Link>
  );
}
